/**
 * DataTable — the first component in web/ui, and the one to get right.
 *
 * WHY IT IS FIRST. Both apps depend on it, and neither library underneath it supplies any of its
 * accessibility. TanStack Table v9 is headless: it gives sorting, filtering, paging, selection,
 * visibility and sizing as STATE AND FUNCTIONS and no markup at all — its own docs say "we're not
 * talking about a literal `<table>` element". Across its 26 guide files the pattern
 * `aria|accessib|a11y|screen ?reader|keyboard|role=|scope=|caption` matches 13 times, of which two
 * are genuine `aria-` references, and its official example ships a non-focusable
 * `<div onClick={header.column.getToggleSortingHandler()}>` with no `aria-sort`. Mantine's Table
 * gives semantic HTML and nothing more — `aria-|role=|scope=` returns 0 hits in its source. So
 * every affordance below is hand-written, and no screen renders a table by hand
 * (docs/ui/03-components.md and :389-392).
 *
 * THE PAGER CANNOT BE NUMBERED. The platform is keyset/cursor everywhere; offset paging exists
 * nowhere. Under `pageCount: -1`, `getPageCount()` returns -1 verbatim (the official example would
 * print "of -1"), `getPageOptions()` returns [], `getCanLastPage()` is always false, and
 * `getCanNextPage()` HARD-RETURNS TRUE and is unusable. So the pager is Prev/Next plus a page-size
 * select, never numbered, never "of N", never Last — and Next is gated on the caller's own
 * `hasNextPage`, which comes from the API's `next_cursor` (docs/ui/03-components.md).
 *
 * ONE STATE-OWNERSHIP MODEL, NOT THREE. v9 offers external atoms, `options.state` plus onChange
 * callbacks, and its internal base atoms. This component uses ATOMS and nothing else, because the
 * URL-as-truth requirement points there: a screen that owns the URL owns the atom, and the table
 * reads through it. Mixing the three is what the track warns against
 * (docs/ui/03-components.md).
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Select,
  Table,
  Text,
  VisuallyHidden,
} from '@mantine/core';
import {
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import type { ColumnDef, Header, RowData } from '@tanstack/react-table';
import { createDataTableAtoms } from './atoms';
import type { DataTableAtoms } from './atoms';
import { useUiText } from '../../texts/ui-texts';
import type { UiTranslate } from '../../texts/ui-texts';
import classes from './DataTable.module.css';

/**
 * The feature set, declared once and statically, as v9's own guidance requires.
 *
 * No `sortedRowModel`, `filteredRowModel` or `paginatedRowModel` factory is registered, and that
 * is correct rather than an omission: sorting, filtering and paging all happen in the API, so the
 * client-side row models would be dead weight that also silently re-sorts a page the server
 * already ordered.
 */
export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  columnVisibilityFeature,
  columnSizingFeature,
  columnResizingFeature,
});

export type DataTableFeatures = typeof dataTableFeatures;

/** How far one arrow-key press moves a column edge, and how far Shift-arrow moves it. */
const RESIZE_STEP_PX = 16;
const RESIZE_STEP_LARGE_PX = 64;
/** No column may be dragged or typed narrower than this. */
const MIN_COLUMN_WIDTH_PX = 48;

export interface DataTableProps<TData extends RowData> {
  /**
   * The table's accessible name. REQUIRED — there is no sensible default, and a table with no
   * name is a table a screen-reader user cannot tell apart from the next one. Rendered as a
   * visually hidden `<caption>`.
   */
  caption: string;
  columns: ReadonlyArray<ColumnDef<DataTableFeatures, TData>>;
  /** ONE page of rows. The API pages by cursor; this is never the whole set. */
  data: ReadonlyArray<TData>;
  /**
   * The state atoms. Pass them in from the screen that owns the URL; omit them and the table
   * creates its own set once, which is the same model with a narrower owner.
   */
  atoms?: DataTableAtoms;
  /** From the API response's `next_cursor`. The library cannot know this and lies if asked. */
  hasNextPage?: boolean;
  /** True once the caller has fetched at least one page backwards from here. */
  hasPreviousPage?: boolean;
  status?: 'ready' | 'loading' | 'error';
  errorMessage?: string;
  emptyMessage?: string;
  /** Stable row identity. Without it, selection breaks the moment a page changes. */
  getRowId?: (row: TData, index: number) => string;
  enableRowSelection?: boolean;
  pageSizeOptions?: readonly number[];
  /** Called after the table has advanced its own page index, so the caller can fetch a cursor. */
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  /** Called when sorting changes, after the page index has been reset to 0. */
  onSortingChange?: () => void;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100] as const;

function sortInstruction(
  next: 'asc' | 'desc' | false,
  columnLabel: string,
  t: UiTranslate,
): string {
  if (next === 'asc') {
    return t('dataTable.sortAscending', { column: columnLabel });
  }
  if (next === 'desc') {
    return t('dataTable.sortDescending', { column: columnLabel });
  }
  return t('dataTable.clearSorting', { column: columnLabel });
}

function headerLabel<TData extends RowData>(
  header: Header<DataTableFeatures, TData, unknown>,
): string {
  const raw = header.column.columnDef.header;
  return typeof raw === 'string' ? raw : header.column.id;
}

export function DataTable<TData extends RowData>({
  caption,
  columns,
  data,
  atoms,
  hasNextPage = false,
  hasPreviousPage = false,
  status = 'ready',
  errorMessage: givenErrorMessage,
  emptyMessage: givenEmptyMessage,
  getRowId,
  enableRowSelection = false,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  onNextPage,
  onPreviousPage,
  onSortingChange,
}: DataTableProps<TData>) {
  const t = useUiText();
  const errorMessage = givenErrorMessage ?? t('dataTable.loadFailed');
  const emptyMessage = givenEmptyMessage ?? t('dataTable.empty');
  // One set of atoms per table instance when the caller does not own them. `useState` with an
  // initialiser, not `useMemo`, because React may drop a `useMemo` value and re-run the factory —
  // which would silently reset the table's state.
  const [ownAtoms] = useState(() => createDataTableAtoms());
  const stateAtoms = atoms ?? ownAtoms;

  const table = useTable<DataTableFeatures, TData>({
    features: dataTableFeatures,
    columns,
    data,
    atoms: stateAtoms,
    // Everything is done in the API. `manualSorting` also stops the table from re-ordering a page
    // the server already ordered.
    manualSorting: true,
    manualPagination: true,
    // -1 is what makes every numbered-pager API return its useless answer. Stated here so the
    // reason the pager below looks the way it does is one line away from the pager.
    pageCount: -1,
    enableRowSelection,
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
    ...(getRowId ? { getRowId } : {}),
  });

  const rows = table.getRowModel().rows;
  const rowCount = rows.length;

  /* -- the header row is ONE Tab stop ---------------------------------------------------------
   * Otherwise a 13-column table costs 13 Tab presses before a reader reaches a row. Every header
   * control — the sort button and the resize handle — takes part in one roving tabindex group;
   * Left and Right move between them and Tab leaves the row entirely.
   * ------------------------------------------------------------------------------------------ */
  const headerControls = useRef<Array<HTMLButtonElement | null>>([]);
  const [rovingIndex, setRovingIndex] = useState(0);
  const restoreFocus = useRef(false);

  const registerControl = useCallback((index: number, element: HTMLButtonElement | null) => {
    headerControls.current[index] = element;
  }, []);

  const moveRoving = useCallback((from: number, delta: number) => {
    const controls = headerControls.current.filter((element): element is HTMLButtonElement =>
      Boolean(element),
    );
    if (controls.length === 0) {
      return;
    }
    const next = (from + delta + controls.length) % controls.length;
    setRovingIndex(next);
    controls[next]?.focus();
  }, []);

  const onHeaderKeyDown = useCallback(
    (index: number) => (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveRoving(index, 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveRoving(index, -1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        moveRoving(index, -index);
      } else if (event.key === 'End') {
        event.preventDefault();
        moveRoving(index, headerControls.current.length - 1 - index);
      }
    },
    [moveRoving],
  );

  /* -- focus stays on the header button when a sort re-renders the rows beneath it ------------ */
  useLayoutEffect(() => {
    if (!restoreFocus.current) {
      return;
    }
    restoreFocus.current = false;
    headerControls.current[rovingIndex]?.focus();
  }, [rovingIndex, data]);

  /* -- pageIndex resets to 0 on any sort, filter or page-size change -------------------------- *
   * Not cosmetic: a cursor chain is ordered, so a sort or a page-size change invalidates every
   * cursor already walked. Leaving pageIndex where it was asks the API for page 4 of a list that
   * no longer has the same page 1 (docs/ui/03-components.md).
   * ------------------------------------------------------------------------------------------ */
  const resetPage = useCallback(() => {
    table.setPageIndex(0);
  }, [table]);

  const toggleSort = useCallback(
    (header: Header<DataTableFeatures, TData, unknown>, multi: boolean) => {
      restoreFocus.current = true;
      header.column.toggleSorting(undefined, multi);
      resetPage();
      onSortingChange?.();
    },
    [onSortingChange, resetPage],
  );

  const resizeColumn = useCallback(
    (columnId: string, deltaPx: number, currentSize: number) => {
      table.setColumnSizing((previous) => ({
        ...previous,
        [columnId]: Math.max(MIN_COLUMN_WIDTH_PX, currentSize + deltaPx),
      }));
    },
    [table],
  );

  /* -- the select-all checkbox's `indeterminate` is a DOM PROPERTY, not an attribute ---------- */
  const selectAllRef = useRef<HTMLInputElement>(null);
  const someSelected = enableRowSelection ? table.getIsSomeRowsSelected() : false;
  const allSelected = enableRowSelection ? table.getIsAllRowsSelected() : false;
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  /* -- the polite result count ---------------------------------------------------------------- *
   * The highest-value announcement in the whole console: after a sort, a filter or a page change
   * the table body is different and nothing else says so. It is `polite`, so it waits its turn,
   * and the element exists at frame zero rather than appearing with the first update — an element
   * that appears at the same moment as its text is announced unreliably.
   * ------------------------------------------------------------------------------------------ */
  const liveMessage = useMemo(() => {
    if (status === 'loading') {
      return t('dataTable.loadingRows');
    }
    if (status === 'error') {
      return errorMessage;
    }
    if (rowCount === 0) {
      return emptyMessage;
    }
    return t('dataTable.rowsShown', { count: rowCount });
  }, [status, errorMessage, emptyMessage, rowCount, t]);

  const resizeHintId = useId();
  const pageSizeId = useId();
  const pagination = table.state.pagination;
  const pageSize = pagination?.pageSize ?? pageSizeOptions[0] ?? 25;

  let controlIndex = -1;

  return (
    <Box className={classes.root}>
      <div role="status" aria-live="polite" aria-atomic="true" className={classes.liveRegion}>
        {liveMessage}
      </div>

      <VisuallyHidden id={resizeHintId}>{t('dataTable.resizeHint')}</VisuallyHidden>

      <Table.ScrollContainer minWidth={480} className={classes.scroller}>
        <Table
          className={classes.table}
          highlightOnHover
          striped={false}
          aria-busy={status === 'loading'}
        >
          {/* A real `<caption>`, not an aria-label: it gives the table its accessible name, it is
              announced with the table, and it survives being copied out of the page. Visually
              hidden is explicitly fine. */}
          <Table.Caption className={classes.caption}>
            <VisuallyHidden>{caption}</VisuallyHidden>
          </Table.Caption>

          <Table.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Tr key={headerGroup.id} className={classes.headerRow}>
                {enableRowSelection ? (
                  <Table.Th scope="col" className={classes.selectCell}>
                    <Checkbox
                      ref={selectAllRef}
                      aria-label={t('dataTable.selectAll')}
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                  </Table.Th>
                ) : null}

                {headerGroup.headers.map((header) => {
                  const label = headerLabel(header);
                  const sorted = header.column.getCanSort() ? header.column.getIsSorted() : false;
                  const canSort = header.column.getCanSort();
                  const canResize = header.column.getCanResize();
                  const sortIndex = canSort ? (controlIndex += 1) : -1;
                  const resizeIndex = canResize ? (controlIndex += 1) : -1;

                  return (
                    <Table.Th
                      key={header.id}
                      /* `scope="col"` on EVERY header cell. Without it a screen reader has to
                         guess whether a cell is a heading for its column or its row. */
                      scope="col"
                      colSpan={header.colSpan}
                      /* The only thing that tells a screen reader the table is sorted at all. */
                      aria-sort={
                        !canSort
                          ? undefined
                          : sorted === 'asc'
                            ? 'ascending'
                            : sorted === 'desc'
                              ? 'descending'
                              : 'none'
                      }
                      data-sorted={sorted === false ? undefined : sorted}
                      style={{ width: `${String(header.getSize())}px` }}
                      className={classes.th}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          ref={(element) => registerControl(sortIndex, element)}
                          tabIndex={sortIndex === rovingIndex ? 0 : -1}
                          onKeyDown={onHeaderKeyDown(sortIndex)}
                          onFocus={() => setRovingIndex(sortIndex)}
                          onClick={(event) => toggleSort(header, event.shiftKey)}
                          className={classes.sortButton}
                        >
                          <table.FlexRender header={header} />
                          {/* The accessible name says what pressing it will DO, which is what
                              `getNextSortingOrder()` is for. */}
                          <VisuallyHidden>
                            {sortInstruction(header.column.getNextSortingOrder(), label, t)}
                          </VisuallyHidden>
                        </button>
                      ) : (
                        <table.FlexRender header={header} />
                      )}

                      {canResize ? (
                        <button
                          type="button"
                          ref={(element) => registerControl(resizeIndex, element)}
                          tabIndex={resizeIndex === rovingIndex ? 0 : -1}
                          aria-label={t('dataTable.resizeColumn', { column: label })}
                          aria-describedby={resizeHintId}
                          className={classes.resizeHandle}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onKeyDown={(event) => {
                            /* The library's own resize handler is pointer and touch only, so a
                               keyboard path does not exist unless we write one. */
                            const step = event.shiftKey ? RESIZE_STEP_LARGE_PX : RESIZE_STEP_PX;
                            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                              event.preventDefault();
                              event.stopPropagation();
                              resizeColumn(
                                header.column.id,
                                event.key === 'ArrowRight' ? step : -step,
                                header.column.getSize(),
                              );
                            } else if (event.key === 'Home') {
                              event.preventDefault();
                              event.stopPropagation();
                              header.column.resetSize();
                            } else {
                              onHeaderKeyDown(resizeIndex)(event);
                            }
                          }}
                          onFocus={() => setRovingIndex(resizeIndex)}
                        />
                      ) : null}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Thead>

          <Table.Tbody>
            {status === 'loading' ? (
              <Table.Tr>
                <Table.Td
                  colSpan={table.getVisibleLeafColumns().length + (enableRowSelection ? 1 : 0)}
                >
                  <Group gap="sm" justify="center" className={classes.stateCell}>
                    <Loader size="sm" />
                    {/* Readable at frame zero: the reduced-motion rule neutralises the spinner's
                        animation, so the word has to carry the meaning on its own. */}
                    <Text size="sm">{t('dataTable.loading')}</Text>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ) : status === 'error' ? (
              <Table.Tr>
                <Table.Td
                  colSpan={table.getVisibleLeafColumns().length + (enableRowSelection ? 1 : 0)}
                >
                  <Alert
                    color="red"
                    variant="light"
                    title={t('dataTable.couldNotLoad')}
                    className={classes.stateCell}
                  >
                    {errorMessage}
                  </Alert>
                </Table.Td>
              </Table.Tr>
            ) : rowCount === 0 ? (
              <Table.Tr>
                <Table.Td
                  colSpan={table.getVisibleLeafColumns().length + (enableRowSelection ? 1 : 0)}
                >
                  <Text size="sm" className={classes.stateCell}>
                    {emptyMessage}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row) => (
                <Table.Tr key={row.id} data-selected={row.getIsSelected() ? '' : undefined}>
                  {enableRowSelection ? (
                    <Table.Td className={classes.selectCell}>
                      <Checkbox
                        aria-label={t('dataTable.selectRow', { id: row.id })}
                        checked={row.getIsSelected()}
                        disabled={!row.getCanSelect()}
                        onChange={row.getToggleSelectedHandler()}
                      />
                    </Table.Td>
                  ) : null}
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>
                      <table.FlexRender cell={cell} />
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {/* THE PAGER. Prev/Next and a page size. There is no page number here and there never can
          be one: see the header comment. */}
      <Group justify="space-between" gap="md" className={classes.pager}>
        <Group gap="xs">
          <label htmlFor={pageSizeId} className={classes.pageSizeLabel}>
            {t('dataTable.rowsPerPage')}
          </label>
          <Select
            id={pageSizeId}
            data={pageSizeOptions.map((size) => String(size))}
            value={String(pageSize)}
            allowDeselect={false}
            w={90}
            onChange={(value) => {
              if (value === null) {
                return;
              }
              table.setPageSize(Number(value));
              // A page-size change invalidates the cursor chain exactly as a sort does.
              resetPage();
            }}
          />
        </Group>

        <Group gap="xs">
          <Button
            variant="subtle"
            disabled={!hasPreviousPage}
            onClick={() => {
              table.previousPage();
              onPreviousPage?.();
            }}
          >
            {t('dataTable.previous')}
          </Button>
          <Button
            variant="subtle"
            /* NOT `table.getCanNextPage()`. Under `pageCount: -1` that hard-returns true, so it
               would leave Next enabled on the last page for ever. */
            disabled={!hasNextPage}
            onClick={() => {
              table.nextPage();
              onNextPage?.();
            }}
          >
            {t('dataTable.next')}
          </Button>
        </Group>
      </Group>
    </Box>
  );
}
